import React, { useEffect, useRef } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { getPatientsList } from "actions/patient-action";
import { setActivePatientRealtimeMrn } from "actions/patient-action";
import { updateOrgCountsAndBalances } from "actions/user-action";
import { deletePatient, updatePatient } from "database/offlineDb/indexDb";
import { getReportStatus } from "helpers/CommonPatient";
import { useMlStatusRepository } from "./mlStatusContext";
import { createPatientStatusAdapter } from "./patientStatusAdapter";
import { patientUploadResyncTargetKey } from "./patientUploadResyncTarget";

const isNonemptyString = (value) =>
  typeof value === "string" && value.trim().length > 0;

const patientUploadPath = (pathname) =>
  pathname === "/patients" ||
  pathname === "/patients/add-patient" ||
  /^\/patients\/update-patient\/[^/]+$/.test(pathname || "");

const imageGuid = (image) =>
  image?.fileData?.guid || image?.data?.guid || image?.guid;

const durableEvidence = (patient) => {
  if (
    !patient?.basicDetailOnServer ||
    !patient?.restDetailOnServer ||
    !patient?.imgOnServer ||
    !patient?.imgDataOnServer
  ) {
    return null;
  }
  const details = patient?.retinalImagesData?.retinalImagesDetails;
  if (!details) return null;
  const keys = [];
  [
    ["left", details.imageLeftEye],
    ["right", details.imageRightEye],
  ].forEach(([laterality, images]) => {
    if (!Array.isArray(images)) return;
    images.forEach((image) => {
      const guid = imageGuid(image);
      if (isNonemptyString(guid)) keys.push(`${guid}|${laterality}`);
    });
  });
  return keys.length > 0 && new Set(keys).size === keys.length
    ? keys.sort()
    : null;
};

const sameMrn = (left, right) =>
  left !== undefined &&
  left !== null &&
  right !== undefined &&
  right !== null &&
  String(left) === String(right);

const SETTLEMENT_TERMINAL_STATUSES = new Set([
  "NRML",
  "NODR",
  "D1",
  "D2",
  "NOF",
  "UND",
  "Error in Preprocess",
]);

const canonicalLaterality = (value) => {
  if (value === "left" || value === "OS") return "left";
  if (value === "right" || value === "OD") return "right";
  return null;
};

const hasSettledAllTuples = (patient, evidence, models) => {
  const images = patient?.backUpReport?.images;
  if (
    !Array.isArray(images) ||
    !Array.isArray(evidence) ||
    evidence.length === 0 ||
    !Array.isArray(models) ||
    models.length === 0
  ) {
    return false;
  }
  const expectedTuples = new Set();
  evidence.forEach((imageEvidence) => {
    models.forEach((model) => {
      expectedTuples.add(`${imageEvidence}|${model}`);
    });
  });
  const observedStatuses = new Map();
  for (const image of images) {
    const laterality = canonicalLaterality(image?.laterality);
    const tuple =
      laterality &&
      isNonemptyString(image?.guid) &&
      isNonemptyString(image?.Model)
        ? `${image.guid}|${laterality}|${image.Model}`
        : null;
    if (!tuple || !expectedTuples.has(tuple)) continue;
    if (observedStatuses.has(tuple)) return false;
    observedStatuses.set(tuple, image.status);
  }
  return (
    observedStatuses.size === expectedTuples.size &&
    Array.from(expectedTuples).every((tuple) =>
      SETTLEMENT_TERMINAL_STATUSES.has(observedStatuses.get(tuple))
    )
  );
};

const configuredAlgorithmEvidence = (algorithms) => {
  const source = Array.isArray(algorithms)
    ? algorithms
    : typeof algorithms === "string"
    ? algorithms.split(",")
    : [];
  if (source.length === 0) return null;
  const values = source.map((algorithm) =>
    typeof algorithm === "string" ? algorithm.trim() : ""
  );
  return values.every(isNonemptyString) && new Set(values).size === values.length
    ? values.sort()
    : null;
};

const durableModelEvidence = (patient, evidence) => {
  const images = patient?.backUpReport?.images;
  if (!Array.isArray(images) || !Array.isArray(evidence) || evidence.length < 1) {
    return null;
  }
  const expectedImages = new Set(evidence);
  const tuples = new Set();
  const models = new Set();
  for (const image of images) {
    const guid = image?.guid;
    const laterality = canonicalLaterality(image?.laterality);
    const model = image?.Model;
    const imageKey = `${guid}|${laterality}`;
    if (
      !isNonemptyString(guid) ||
      !laterality ||
      !isNonemptyString(model) ||
      !expectedImages.has(imageKey)
    ) {
      return null;
    }
    const tuple = `${imageKey}|${model}`;
    if (tuples.has(tuple)) return null;
    tuples.add(tuple);
    models.add(model);
  }
  if (models.size < 1) return null;
  const orderedModels = Array.from(models).sort();
  return evidence.every((imageKey) =>
    orderedModels.every((model) => tuples.has(`${imageKey}|${model}`))
  )
    ? orderedModels
    : null;
};

export const PatientUploadRealtimeOwner = ({
  activeMrn,
  algorithms,
  authToken,
  clearActiveMrn,
  pendingMrn,
  location,
  patients = [],
  republish,
  snapshotPatient,
  updateBalances,
  userStore,
  deletePatientRow = deletePatient,
  writePatient = updatePatient,
  adapterFactory = createPatientStatusAdapter,
}) => {
  const repository = useMlStatusRepository();
  const pathname = location?.pathname || "";
  const authTokenRef = useRef(authToken);
  const hasAuthenticatedRef = useRef(
    authToken !== undefined && authToken !== null
  );
  const clearActiveMrnRef = useRef(clearActiveMrn);
  const authChanged =
    hasAuthenticatedRef.current && authTokenRef.current !== authToken;
  const activePatientMatches = patientUploadPath(pathname) && !authChanged
    ? patients.filter((patient) => sameMrn(patient?.mrn, activeMrn))
    : [];
  // Durable resume on /patients must not depend on HOW the user arrived here.
  // A browser reload and an in-session navigation (edit another patient, then
  // exit) leave identical durable state, so the departed-workflow latch that
  // used to gate this filter silently tore down resume scopes on the next
  // re-render after landing on /patients — dropping later ML status events
  // for still-processing images until a manual refresh (advenio-7qp8).
  const durablePatients =
    pathname === "/patients" && !authChanged
      ? patients.filter((patient) => durableEvidence(patient))
      : [];
  // A durable row only needs a resume when its status is genuinely unknown:
  // a retryable tuple (Pending/Unprocessed/Error-CNF) or a missing/uncertain
  // backUpReport. Fully terminal rows are already settled and must not spawn
  // scopes or snapshot HTTP. Duplicate MRNs still fail closed.
  const uncertainDurablePatients = durablePatients.filter((patient) => {
    const images = patient?.backUpReport?.images;
    if (!Array.isArray(images)) return true;
    const expected = new Set(durableEvidence(patient));
    const models = durableModelEvidence(patient, Array.from(expected).sort());
    if (!models || models.length === 0) return true;
    const expectedTuples = new Set();
    Array.from(expected).forEach((imageKey) => {
      models.forEach((model) => expectedTuples.add(`${imageKey}|${model}`));
    });
    const observed = new Set();
    for (const image of images) {
      const laterality = canonicalLaterality(image?.laterality);
      const guid = image?.guid;
      if (!laterality || !isNonemptyString(guid)) return true;
      const imageKey = `${guid}|${laterality}`;
      if (!expected.has(imageKey)) return true;
      const tuple = `${imageKey}|${image?.Model}`;
      if (observed.has(tuple)) return true;
      observed.add(tuple);
      if (!SETTLEMENT_TERMINAL_STATUSES.has(image?.status)) return true;
    }
    return observed.size !== expectedTuples.size;
  });
  const mrnCounts = new Map();
  uncertainDurablePatients.forEach((patient) => {
    const key = String(patient?.mrn);
    mrnCounts.set(key, (mrnCounts.get(key) || 0) + 1);
  });
  // A browser reload clears Redux's transient active MRN, but Dexie retains
  // the durable upload row. Resume each uncertain row on its own scope; a
  // duplicated MRN remains ambiguous and is skipped rather than guessed.
  // A realtime target that no longer matches any listed patient is stale:
  // exiting an edit whose patient settled and was removed leaves activeMrn
  // (or pendingMrn) pointing at a vanished row, and /patients never clears
  // it. Treating a vanished target as absent restores the resume scope —
  // the events missed during that workflow can never be re-delivered, so
  // the scope's snapshot is the only recovery (advenio-7qp8).
  const staleActiveTarget =
    activeMrn !== undefined &&
    activeMrn !== null &&
    activePatientMatches.length === 0;
  const stalePendingTarget =
    pendingMrn !== undefined &&
    pendingMrn !== null &&
    !patients.some((candidate) => sameMrn(candidate?.mrn, pendingMrn));
  const durableResumeCandidates =
    activePatientMatches.length === 0 &&
    (activeMrn === undefined ||
      activeMrn === null ||
      staleActiveTarget) &&
    (pendingMrn === undefined ||
      pendingMrn === null ||
      stalePendingTarget)
      ? uncertainDurablePatients.filter(
          (patient) => mrnCounts.get(String(patient?.mrn)) === 1
        )
      : [];
  const durableResumeCandidate =
    durableResumeCandidates.length === 1 ? durableResumeCandidates[0] : null;
  const activePatient = activePatientMatches.length === 1
    ? activePatientMatches[0]
    : durableResumeCandidate;
  // A same-session scope lived through every upload moment, so socket events
  // alone settle it. Only a durable resume (browser reload mid-processing)
  // missed those events and needs the one-shot authoritative snapshot.
  const durableResume = durableResumeCandidate !== null;
  const evidence = durableEvidence(activePatient);
  const configuredAlgorithms = configuredAlgorithmEvidence(algorithms);
  // The queued result uses the persisted pipeline model name, which can be a
  // versioned form of the user's configured display algorithm.  Once the
  // durable report identifies that exact tuple set, it is the safe authority
  // for subscription and settlement.
  const persistedModels = durableModelEvidence(activePatient, evidence);
  const resultModels =
    persistedModels &&
    configuredAlgorithms &&
    persistedModels.length === configuredAlgorithms.length
      ? persistedModels
      : configuredAlgorithms;
  // A multi-row durable resume derives its per-scope keys inside the subscribe
  // effect; the outer scopeKey only arms the effect for any resume at all.
  const scopeKey = (evidence && resultModels) || durableResumeCandidates.length > 0
    ? `patient-upload:${String(activePatient?.mrn)}:${(evidence || durableEvidence(durableResumeCandidates[0]) || ["multi"]).join(",")}:${(resultModels || configuredAlgorithmEvidence(algorithms) || ["resume"]).join(",")}`
    : null;
  const patientRef = useRef(activePatient);
  const algorithmsRef = useRef(algorithms);
  const durableCandidatesRef = useRef(durableResumeCandidates);
  const patientsRef = useRef(patients);
  const republishRef = useRef(republish);
  const snapshotRef = useRef(snapshotPatient);
  const updateBalancesRef = useRef(updateBalances);
  const userStoreRef = useRef(userStore);
  const writePatientRef = useRef(writePatient);
  const deletePatientRowRef = useRef(deletePatientRow);
  const adapterFactoryRef = useRef(adapterFactory);
  patientRef.current = activePatient;
  algorithmsRef.current = resultModels || algorithms;
  durableCandidatesRef.current = durableResumeCandidates;
  patientsRef.current = patients;
  clearActiveMrnRef.current = clearActiveMrn;
  republishRef.current = republish;
  snapshotRef.current = snapshotPatient;
  updateBalancesRef.current = updateBalances;
  userStoreRef.current = userStore;
  writePatientRef.current = writePatient;
  deletePatientRowRef.current = deletePatientRow;
  adapterFactoryRef.current = adapterFactory;

  useEffect(() => {
    const hasRealtimeTarget =
      (activeMrn !== undefined && activeMrn !== null) ||
      (pendingMrn !== undefined && pendingMrn !== null);
    if (
      hasAuthenticatedRef.current &&
      authTokenRef.current !== authToken &&
      hasRealtimeTarget &&
      typeof clearActiveMrn === "function"
    ) {
      clearActiveMrn(null);
    }
    authTokenRef.current = authToken;
    if (authToken !== undefined && authToken !== null) {
      hasAuthenticatedRef.current = true;
    }
  }, [activeMrn, authToken, clearActiveMrn, pendingMrn]);

  useEffect(() => {
    const hasRealtimeTarget =
      (activeMrn !== undefined && activeMrn !== null) ||
      (pendingMrn !== undefined && pendingMrn !== null);
    if (
      !patientUploadPath(pathname) &&
      hasRealtimeTarget &&
      typeof clearActiveMrn === "function"
    ) {
      clearActiveMrn(null);
    }
  }, [activeMrn, clearActiveMrn, pathname, pendingMrn]);

  useEffect(() => {
    if (!repository || !scopeKey) return undefined;
    let writes = Promise.resolve();
    const writePatientInOrder = (patient) => {
      const operation = writes.then(() => writePatientRef.current(patient));
      writes = operation.catch((error) => { console.warn("PatientUploadRealtimeOwner.js queue continuation suppressed previous failure", error); });
      return operation;
    };
    const configuredAlgorithms = configuredAlgorithmEvidence(algorithmsRef.current);
    const resumeCandidates = durableCandidatesRef.current;
    const scopeCount = resumeCandidates.length > 0
      ? resumeCandidates.length
      : 1;
    let active = true;
    const unsubscribes = [];
    const scopeAdapters = [];
    for (let index = 0; index < scopeCount; index += 1) {
      const scopePatient = resumeCandidates[index] || patientRef.current;
      if (!scopePatient) continue;
      const scopeMrn = scopePatient.mrn;
      // Projections must see the live patient (Redux re-publishes after every
      // write), so the scope reads by MRN instead of freezing one object.
      const liveScopePatient = resumeCandidates.length > 0
        ? () =>
            patientsRef.current.find((candidate) =>
              sameMrn(candidate?.mrn, scopeMrn)
            ) || null
        : () => patientRef.current;
      const scopeEvidence = durableEvidence(scopePatient);
      const scopePersistedModels = durableModelEvidence(
        scopePatient,
        scopeEvidence
      );
      const scopeModels =
        scopePersistedModels &&
        configuredAlgorithms &&
        scopePersistedModels.length === configuredAlgorithms.length
          ? scopePersistedModels
          : configuredAlgorithms;
      if (!scopeEvidence || !scopeModels) continue;
      const scopeId = `patient-upload:${String(scopePatient.mrn)}:${scopeEvidence.join(",")}:${scopeModels.join(",")}`;
      const acceptedScopeEvidence = new Set(scopeEvidence);
      const acceptedScopeModels = new Set(scopeModels);
      const scopeAdapter = adapterFactoryRef.current({
        writePatient: writePatientInOrder,
        republish: (scope) => republishRef.current(scope),
      });
      scopeAdapters.push(scopeAdapter);
      let scopeUnsubscribed = false;
      let scopeRetired = false;
      let scopeBalanceAttempted = false;
      let scopeDeletedTerminalMrn = null;
      let scopeEventGeneration = 0;
      let scopeSnapshotGeneration = 0;
      let scopeSettlementEvaluations = Promise.resolve();
      const retireScope = () => {
        if (scopeRetired) return;
        scopeRetired = true;
        if (!scopeUnsubscribed && typeof unsubscribeCurrent === "function") {
          scopeUnsubscribed = true;
          unsubscribeCurrent();
        }
        scopeAdapter.cleanup();
      };
      const publishScopeDeletionAndRetire = async (context) => {
        try {
          await republishRef.current(context);
        } catch (error) {
          return false;
        }
        if (scopeRetired || !context.isCurrent()) return false;
        if (typeof clearActiveMrnRef.current === "function") {
          clearActiveMrnRef.current(null);
        }
        retireScope();
        return true;
      };
      const evaluateScopeSettlement = (candidatePatient, context) => {
        const evaluation = scopeSettlementEvaluations
          .catch((error) => {
            console.warn(
              "PatientUploadRealtimeOwner.js settlement continuation suppressed previous failure",
              error
            );
          })
          .then(async () => {
            if (
              scopeRetired ||
              !context.isCurrent() ||
              !hasSettledAllTuples(
                candidatePatient,
                scopeEvidence,
                scopeModels
              )
            ) {
              return false;
            }
            if (!scopeBalanceAttempted) {
              scopeBalanceAttempted = true;
              if (typeof updateBalancesRef.current === "function") {
                try {
                  await updateBalancesRef.current(context);
                } catch (error) {
                  // Entitlement refresh cannot invalidate the durable status write.
                }
              }
            }
            if (scopeRetired || !context.isCurrent()) return false;
            if (scopeDeletedTerminalMrn === null) {
              try {
                await deletePatientRowRef.current(candidatePatient.mrn);
                scopeDeletedTerminalMrn = candidatePatient.mrn;
              } catch (error) {
                return false;
              }
            }
            return publishScopeDeletionAndRetire(context);
          });
        scopeSettlementEvaluations = evaluation;
        return evaluation;
      };
      const runScopeSnapshot = async (context) => {
        const acceptedEventGeneration = scopeEventGeneration;
        const acceptedSnapshotGeneration = ++scopeSnapshotGeneration;
        const snapshotContext = {
          ...context,
          isCurrent: () =>
            context.isCurrent() &&
            scopeEventGeneration === acceptedEventGeneration &&
            scopeSnapshotGeneration === acceptedSnapshotGeneration,
        };
        if (scopeDeletedTerminalMrn !== null) {
          const published = await publishScopeDeletionAndRetire(snapshotContext);
          return { outcome: published ? "applied" : "fallback" };
        }
        const patient = liveScopePatient();
        if (!patient || !snapshotContext.isCurrent()) {
          return Promise.resolve({ outcome: "ignored" });
        }
        let persistedPatient = null;
        const writeSnapshotInOrder = async (nextPatient) => {
          const result = await writePatientInOrder(nextPatient);
          if (result && result?.outcome !== "ignored") {
            persistedPatient = nextPatient;
          }
          return result;
        };
        let result;
        if (typeof snapshotRef.current === "function") {
          result = await snapshotRef.current(
            patient,
            snapshotContext,
            writeSnapshotInOrder
          );
        } else {
          result = await getReportStatus(
            patient,
            true,
            {
              userStore: userStoreRef.current,
              getPatientsList: () => republishRef.current(snapshotContext),
              writePatient: writeSnapshotInOrder,
            },
            false,
            snapshotContext
          );
        }
        const acceptedResult =
          result !== false &&
          result?.outcome !== "ignored" &&
          result?.outcome !== "fallback";
        if (acceptedResult && snapshotContext.isCurrent()) {
          await evaluateScopeSettlement(persistedPatient, snapshotContext);
        }
        return result;
      };
      const unsubscribeCurrent = repository.subscribe({
        scopeKey: scopeId,
        resyncTargetKey: patientUploadResyncTargetKey(scopePatient.mrn),
        accepts: (event) =>
          acceptedScopeEvidence.has(`${event?.guid}|${event?.laterality}`) &&
          acceptedScopeModels.has(event?.model),
        initialSnapshot:
          resumeCandidates.length > 0 ? runScopeSnapshot : undefined,
        project: async (event, context) => {
          scopeEventGeneration += 1;
          const result = await scopeAdapter.apply({
            getPatient: liveScopePatient,
            event,
            algorithms: scopeModels,
            isCurrent: context.isCurrent,
          });
          if (result?.outcome === "applied" && context.isCurrent()) {
            await evaluateScopeSettlement(result.patient, context);
          }
          return result;
        },
        resync: runScopeSnapshot,
      });
      unsubscribes.push(() => {
        scopeUnsubscribed = true;
        unsubscribeCurrent();
      });
    }
    return () => {
      active = false;
      unsubscribes.forEach((unsubscribe) => unsubscribe());
      scopeAdapters.forEach((adapter) => adapter.cleanup());
    };
  }, [repository, scopeKey]);

  return null;
};

const mapStateToProps = (store) => ({
  activeMrn: store.patientStore?.activeRealtimePatientMrn,
  pendingMrn: store.patientStore?.pendingRealtimePatientMrn,
  algorithms: store.userStore?.userSettings?.algorithms || [],
  patients: store.patientStore?.patientsList || [],
  userStore: store.userStore,
});

export default connect(mapStateToProps, {
  clearActiveMrn: setActivePatientRealtimeMrn,
  republish: getPatientsList,
  updateBalances: updateOrgCountsAndBalances,
})(withRouter(PatientUploadRealtimeOwner));
